import React, { useMemo } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, Cell } from 'recharts';
import type { CardComparativo } from '../types';

interface Props {
  detalheMensal: NonNullable<CardComparativo['detalheMensal']>;
  formato: CardComparativo['formato'];
  valorAtual: number;
  valorAnterior: number;
  extraAtual?: string;
  extraAnterior?: string;
}

const COR_PERIODO_A = '#5F6C37';
const COR_PERIODO_B = '#F6A937';

const fmtBRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });
const fmtNum = (v: number) => v.toLocaleString('pt-BR');
const fmtPct = (v: number) => `${(v * 100).toFixed(1)}%`;
const fmtValor = (v: number, formato: CardComparativo['formato']) =>
  formato === 'percentual' ? fmtPct(v) : formato === 'numero' ? fmtNum(v) : fmtBRL(v);

interface ItemGrafico {
  mesReferencia: string;
  periodo: 'A' | 'B' | 'gap';
  valor: number;
}

const GraficoDetalheMensal: React.FC<Props> = ({ detalheMensal, formato, valorAtual, valorAnterior, extraAtual, extraAnterior }) => {
  const mesesA = detalheMensal.filter((d) => d.periodo === 'A');
  const mesesB = detalheMensal.filter((d) => d.periodo === 'B');

  // Insere uma barra invisível (valor 0, sem cor) entre os dois grupos — é o jeito de fazer o
  // recharts abrir um espaço maior só ENTRE os períodos, já que barCategoryGap se aplica igual
  // entre todas as categorias. Os meses de um mesmo período ficam mais colados entre si.
  const dadosComGap: ItemGrafico[] = useMemo(() => {
    if (mesesA.length === 0 || mesesB.length === 0) return detalheMensal;
    return [...mesesA, { mesReferencia: '', periodo: 'gap', valor: 0 }, ...mesesB];
  }, [detalheMensal, mesesA, mesesB]);

  // Legenda sob o gráfico (total + extra por período) usa a mesma proporção de largura que
  // cada grupo ocupa no gráfico (nº de meses daquele período ÷ total de "slots", incluindo o
  // slot do gap), pra ficar alinhada com o respectivo grupo de barras.
  const totalSlots = dadosComGap.length || 1;
  const temGap = mesesA.length > 0 && mesesB.length > 0;
  const larguraA = (mesesA.length / totalSlots) * 100;
  const larguraGap = temGap ? (1 / totalSlots) * 100 : 0;
  const larguraB = (mesesB.length / totalSlots) * 100;

  const TooltipCustom = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const item = payload[0].payload as ItemGrafico;
    if (item.periodo === 'gap') return null;
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-lg px-2.5 py-1.5 shadow-xl text-[11px]">
        <p className="font-bold text-gray-800 dark:text-white">
          {item.mesReferencia} <span className="font-normal text-gray-400">(Período {item.periodo})</span>
        </p>
        <p className="font-semibold" style={{ color: item.periodo === 'A' ? COR_PERIODO_A : COR_PERIODO_B }}>
          {fmtValor(item.valor, formato)}
        </p>
      </div>
    );
  };

  return (
    <div className="mt-2 -mx-1">
      <ResponsiveContainer width="100%" height={130}>
        <BarChart data={dadosComGap} margin={{ left: 0, right: 0, top: 4, bottom: 0 }} barCategoryGap="8%">
          <XAxis
            dataKey="mesReferencia"
            tickFormatter={(v: string) => (v ? v.split('/')[0] : '')}
            tick={{ fontSize: 9 }}
            stroke="#9ca3af"
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<TooltipCustom />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
          <Bar dataKey="valor" radius={[3, 3, 0, 0]}>
            {dadosComGap.map((d, i) => (
              <Cell
                key={`${d.periodo}-${d.mesReferencia}-${i}`}
                fill={d.periodo === 'A' ? COR_PERIODO_A : d.periodo === 'B' ? COR_PERIODO_B : 'transparent'}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <div className="flex text-center -mt-1">
        <div style={{ flexBasis: `${larguraA}%` }} className="shrink-0 min-w-0">
          <p className="text-[11px] font-bold truncate" style={{ color: COR_PERIODO_A }}>{fmtValor(valorAtual, formato)}</p>
          {extraAtual && <p className="text-[9px] text-gray-400 truncate">{extraAtual}</p>}
        </div>
        {temGap && <div style={{ flexBasis: `${larguraGap}%` }} className="shrink-0" />}
        <div style={{ flexBasis: `${larguraB}%` }} className="shrink-0 min-w-0">
          <p className="text-[11px] font-bold truncate" style={{ color: COR_PERIODO_B }}>{fmtValor(valorAnterior, formato)}</p>
          {extraAnterior && <p className="text-[9px] text-gray-400 truncate">{extraAnterior}</p>}
        </div>
      </div>
    </div>
  );
};

export default GraficoDetalheMensal;
